// knowledgeDetails.pipeline.retrieval.* — owned by the retrieval page.
export default {
  intro:
    'Ask a question to see which chunks this knowledge base returns, in what order, and why.',
  notSaved:
    'Settings here are not saved. To use them, set the same values in your chat assistant or Retrieval agent.',
  weightSplit: 'keyword {{keyword}} / vector {{vector}}',
  weightSplitRerank: 'keyword {{keyword}} / rerank {{vector}}',
  labelsTitle: 'Tags matched by the question',
  index: '{{model}} · {{n}} chunks in index',
  summary: '{{total}} chunks scored at or above {{threshold}}',
  summaryFiltered: '{{total}} chunks in the selected documents',
  filterNoThreshold:
    'With a document filter on, the similarity threshold is not applied.',
  docFilter: 'Filter by document',
  docFilterSelected: '{{selected}} of {{total}} selected',
  docNotFound: 'Could not find {{name}} in this knowledge base.',
  clearFilter: 'Clear',
  formulaTitle: 'How the score is built',
  formula: 'hybrid = {{vw}} × vector + {{tw}} × term + rank feature',
  formulaRerank:
    'hybrid = {{tw}} × (term + rank feature) + {{vw}} × rerank score',
  formulaFor: 'for #{{rank}}:',
  rankFeatureHint:
    'Rank feature = page rank + tag match × 10. It is the part of the score that term and vector do not explain. Select a result to see its numbers.',
  legend: {
    vector: 'vector',
    rerank: 'rerank score',
    term: 'term',
    rank: 'rank feature',
  },
  scores: {
    rerank: 'Rerank score',
  },
  barsLabel: 'Ranked chunks',
  scale:
    'Bar length = hybrid score × 100, on a scale of 0 to {{max}}. The dashed line is the similarity threshold.',
  page: 'p.{{page}}',
  graphChunk: 'Knowledge graph context',
  graphChunkHint:
    'Assembled from the knowledge graph and placed first. Its score is fixed, not measured, so it is not ranked.',
  detail: {
    document: 'Document',
    page: 'Page',
    path: 'Path',
    chunkId: 'Chunk id',
    keywords: 'Keywords (× 5)',
    excluded: 'Excluded keywords',
    none: 'none',
  },
  testText: 'Test text',
  runTest: 'Test',
  topK: 'Top-K',
  topKHint: 'kNN candidates',
  vectorComplement: 'vector',
  rerankComplement: 'rerank',
  notSavedShort: 'Settings here are not saved.',
  rankedTitle: 'Ranked chunks',
  colRank: '#',
  colHybrid: 'hybrid',
  colDoc: 'document · page',
  thresholdAxis: 'threshold {{value}}',
  moreAbove: '{{count}} more chunks ≥ {{threshold}}',
  barScale: 'bar length = hybrid score; scale 0–{{max}}',
  previewHead: '#{{rank}} · {{doc}}',
  why: {
    first: '#{{rank}} · why it ranks first',
    here: '#{{rank}} · why it ranks here',
    region: 'Region',
    regionValue: 'x {{x0}}–{{x1}} · y {{y0}}–{{y1}}',
    termVector: 'Term · vector',
    termRerank: 'Term · rerank',
    rankFeature: 'Rank feature',
    prefixes: 'Prefixes added',
    image: 'Image',
    imageYes: 'yes (figure or page image)',
  },
  flow: {
    label: 'How this query was answered',
    query: 'Query',
    queryHint: 'as typed',
    words: '{{count}} words',
    embed: 'Embed',
    oneVector: '1 query vector',
    termsPass:
      'your words go to full-text search as typed (top lane); the question is embedded once',
    termsLane: 'keywords · not embedded',
    bm25Lane: 'full-text · boost {{boost}}',
    hybrid: 'Hybrid search',
    hybridHint: 'two lanes',
    knn: 'kNN k = {{k}}',
    knnLane: 'kNN k = {{k}} · boost {{boost}}',
    fixedBlend: 'the engine blend is fixed; your sliders act in Rescore',
    rescore: 'Rescore',
    rescoreHint: 'your weights apply here',
    rescoreBox: 'hybrid = {{vw}} × vector\n+ {{tw}} × term\n+ rank feature',
    rescoreBoxRerank:
      'hybrid = {{vw}} × rerank\n+ {{tw}} × term\n+ {{tw}} × rank feature',
    termIs:
      'term = weighted token overlap (title × 2, keywords × 5, questions × 6) · vector = cosine',
    threshold: 'Threshold',
    thresholdHint: 'hybrid ≥ {{value}}',
    pass: 'pass',
    thresholdOff: 'not applied',
    thresholdOffHint: 'document filter on',
    results: 'Results',
    resultsHint: 'page {{page}} · {{shown}} of {{total}}',
    shown: '{{count}} shown',
    more: '{{count}} more',
  },
  explainer: {
    title: 'How a retrieval test is scored',
    intro:
      'Conduct a retrieval test to check if Discover can recover the intended content for the LLM. Every number on this page comes from the last run against {{kb}}: threshold {{threshold}}, keyword weight {{keyword}}, Top-K {{topK}}, {{rerank}}, knowledge graph {{kg}}.',
    noRerank: 'no rerank model',
    rerankModel: 'rerank model {{model}}',
    on: 'on',
    off: 'off',
    step1: 'Similarity threshold',
    inThisRun: 'In this run:',
    step1Extra:
      'On the bars above it is the dashed line; {{total}} chunks clear it.',
    step2: 'Keyword similarity weight',
    step2Extra:
      'The complement is the vector weight ({{vector}} here). It is shown next to the slider because the engine stores the slider as 1 − value.',
    step3: 'Rerank model',
    step3Extra:
      'With a rerank model selected, the vector segment of every bar becomes the rerank score and the legend relabels itself.',
    step4: 'Top-K',
    step4Extra:
      'It is also the k of the kNN lane on every search, rerank or not, which is why it is always visible here.',
    step5: 'Use knowledge graph',
    step5Extra:
      'When it is on, the knowledge-graph context is placed first as its own card; its score is fixed, so it is not ranked.',
    constants: 'Constants you should know',
    constantsBody:
      'The engine blends its two lanes at fixed boosts (full-text {{bm25}} / kNN {{knn}}) before your weights are applied in Rescore. Inside a chunk the title counts × 2, keywords × 5 and questions × 6. If nothing matches, the engine retries once with a looser text match ({{minMatch}}) and a kNN floor of {{floor}}.',
  },
};
